import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Mail, Loader2, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function EmailSettingsCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get("/me/settings").then((r) => setS(r.data)).catch(() => {}); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/settings", { email_reports: s.email_reports, report_email: s.report_email, ...patch });
      setS(data);
      toast.success(data.email_reports ? "Email reports enabled." : "Email reports disabled.");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/me/settings/test-email");
      toast.success(`Test report sent to ${data.to}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not send"); } finally { setTesting(false); }
  };

  if (!s) return null;
  const configured = s.email_pipeline?.configured;

  return (
    <div className="card-soft p-6" data-testid="email-settings-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#7C3AED]" />
          <h3 className="font-heading font-semibold text-lg text-[#1E1560]">Email reports</h3>
        </div>
        <Switch checked={s.email_reports} disabled={saving} onCheckedChange={(v) => { setS({ ...s, email_reports: v }); save({ email_reports: v }); }} data-testid="email-reports-switch" />
      </div>
      <p className="text-sm text-secondary mb-4">Receive the PDF release-readiness report by email after every manual or scheduled run.</p>
      <div className="flex gap-2">
        <Input value={s.report_email || ""} onChange={(e) => setS({ ...s, report_email: e.target.value })} placeholder="reports@yourcompany.com"
          data-testid="report-email-input" className="rounded-lg" />
        <Button variant="outline" onClick={() => save({})} disabled={saving} data-testid="report-email-save" className="rounded-lg border-border">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
      <div className={`mt-4 flex items-start gap-2 text-xs rounded-lg p-3 ${configured ? "bg-[#0F9D77]/10 text-[#0F9D77]" : "bg-[#FF8A3D]/10 text-[#B45309]"}`} data-testid="email-pipeline-status">
        {configured ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
        <span>{configured
          ? `Delivery active via ${s.email_pipeline.provider} from ${s.email_pipeline.sender}.`
          : "Email delivery is not yet configured by the platform admin — your reports stay available in-app (bell icon and Dashboard) until it is switched on."}</span>
      </div>
      <Button onClick={sendTest} disabled={testing || !configured} data-testid="send-test-report-btn" variant="ghost"
        className="mt-3 rounded-full text-[#3A2DB8] hover:bg-[#3A2DB8]/10 gap-2 px-3">
        {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send me my latest report
      </Button>
    </div>
  );
}
